import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Paper,
  Typography,
  Box,
  Grid,
  CircularProgress,
  Divider,
  Chip,
  IconButton,
  List,
  Alert,
  AlertTitle,
} from "@mui/material";
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
} from "@mui/icons-material";

export default function CC446({ submitForm, declarationInfo }) {
  const [loading, setLoading] = useState(true);

  const [messageSender, setMessageSender] = useState("");
  const [messageRecipient, setMessageRecipient] = useState("");

  const [cc438RequestedDocs, setCc438RequestedDocs] = useState([]);
  const [selectedDocIndex, setSelectedDocIndex] = useState(0);

  const [sequenceNumber, setSequenceNumber] = useState("");
  const [documentType, setDocumentType] = useState("");
  const [description, setDescription] = useState("");
  const [requestDate, setRequestDate] = useState("");

  const [justification, setJustification] = useState("");
  const [documents, setDocuments] = useState([]);

  useEffect(() => {
    if (declarationInfo) {
      if (declarationInfo.declaration_info) {
        setMessageSender(declarationInfo.declaration_info.message_sender || "");
        setMessageRecipient(
          declarationInfo.declaration_info.message_recipient || ""
        );
      }

      if (
        declarationInfo.cc438_info_list &&
        declarationInfo.cc438_info_list.length > 0
      ) {
        setCc438RequestedDocs(declarationInfo.cc438_info_list);

        const firstDoc = declarationInfo.cc438_info_list[0];
        setSequenceNumber(firstDoc.sequence_number || "");
        setDocumentType(firstDoc.document_type || "");
        setDescription(firstDoc.description || "");
        setRequestDate(firstDoc.request_date || "");
      }
    }
    setLoading(false);
  }, [declarationInfo]);

  const handleSelectRequestedDocument = (index) => {
    setSelectedDocIndex(index);
    const selectedDoc = cc438RequestedDocs[index];
    if (selectedDoc) {
      setSequenceNumber(selectedDoc.sequence_number || "");
      setDocumentType(selectedDoc.document_type || "");
      setDescription(selectedDoc.description || "");
      setRequestDate(selectedDoc.request_date || "");
    }
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64Content = e.target.result.split(",")[1];

        const newDocument = {
          sequence_number: String(documents.length + 1),
          file_type: file.type || "application/octet-stream",
          file_content: base64Content,
          file_name: file.name,
        };

        setDocuments((prev) => [...prev, newDocument]);
      };
      reader.readAsDataURL(file);
    });

    event.target.value = "";
  };

  const handleDeleteDocument = (index) => {
    setDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDocumentFieldChange = (index, field, value) => {
    setDocuments((prev) =>
      prev.map((doc, i) => (i === index ? { ...doc, [field]: value } : doc))
    );
  };

  const handleSubmit = () => {
    const requestedDocumentsPayload =
      cc438RequestedDocs.length > 0
        ? cc438RequestedDocs
            .filter((doc) => doc.sequence_number && doc.document_type)
            .map((doc) => ({
              sequence_number: doc.sequence_number,
              type: doc.document_type,
              justification,
              documents: documents.length > 0 ? documents : undefined,
            }))
        : [
            {
              sequence_number: sequenceNumber,
              type: documentType,
              justification,
              documents: documents.length > 0 ? documents : undefined,
            },
          ];

    const requestData = {
      sequence_number: sequenceNumber,
      type: documentType,
      justification: justification,
      documents: documents.length > 0 ? documents : undefined,
      requested_documents: requestedDocumentsPayload,
    };

    submitForm({ messageType: "CC446", data: requestData });
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!declarationInfo) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <Typography color="error">Failed to load declaration data</Typography>
      </Box>
    );
  }

  return (
    <div style={{ margin: "20px 0" }}>
      <Paper
        sx={{ boxShadow: "none" }}
        style={{
          padding: "20px",
          marginBottom: "20px",
          backgroundColor: "#f5f5f5",
        }}
      >
        <Typography variant="h6" gutterBottom>
          Declaration Information
        </Typography>
        <Divider style={{ marginBottom: "15px" }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Message Sender"
              value={messageSender}
              onChange={(e) => setMessageSender(e.target.value)}
              fullWidth
              required
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Message Recipient"
              value={messageRecipient}
              onChange={(e) => setMessageRecipient(e.target.value)}
              fullWidth
              required
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              MRN
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body1" style={{ fontWeight: 500 }}>
                {declarationInfo?.mrn || "N/A"}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Message Type
            </Typography>
            <Chip label="CC446" color="primary" />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Preparation Date & Time
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Will be automatically set to current moment when submitting
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {cc438RequestedDocs.length > 0 && (
        <Alert
          severity="info"
          icon={<InfoIcon />}
          style={{ marginBottom: "20px" }}
        >
          <AlertTitle>
            CC438 - Requested Documents
            {cc438RequestedDocs.length > 1 &&
              ` (${cc438RequestedDocs.length} documents found)`}
          </AlertTitle>

          {cc438RequestedDocs.length > 1 && (
            <Box mb={2}>
              <Typography variant="body2" gutterBottom>
                <strong>Select the document you want to respond to:</strong>
              </Typography>
              <Grid container spacing={1}>
                {cc438RequestedDocs.map((doc, index) => (
                  <Grid item key={index}>
                    <Chip
                      label={`Doc ${index + 1}: Type ${
                        doc.document_type || "N/A"
                      }`}
                      color={selectedDocIndex === index ? "primary" : "default"}
                      onClick={() => handleSelectRequestedDocument(index)}
                      clickable
                    />
                  </Grid>
                ))}
              </Grid>
              <Divider style={{ margin: "15px 0" }} />
            </Box>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>Sequence Number:</strong> {sequenceNumber || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>Document Type:</strong> {documentType || "N/A"}
              </Typography>
            </Grid>
            {description && (
              <Grid item xs={12}>
                <Typography variant="body2">
                  <strong>Description:</strong> {description}
                </Typography>
              </Grid>
            )}
            {requestDate && (
              <Grid item xs={12}>
                <Typography variant="body2">
                  <strong>Request Date:</strong> {requestDate}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Alert>
      )}

      <Paper
        sx={{ boxShadow: "none" }}
        style={{ padding: "20px", marginBottom: "20px" }}
      >
        <Typography variant="h6" gutterBottom>
          CC446 - Transmitere documente
        </Typography>
        <Divider style={{ marginBottom: "15px" }} />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Sequence Number"
              value={sequenceNumber}
              onChange={(e) => setSequenceNumber(e.target.value)}
              fullWidth
              required
              disabled
              helperText="From CC438 request"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Document Type"
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
              fullWidth
              required
              disabled
              helperText="From CC438 request"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Justification"
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              fullWidth
              required
              multiline
              rows={4}
              placeholder="Enter justification for submitting requested documents..."
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper
        sx={{ boxShadow: "none" }}
        style={{ padding: "20px", marginBottom: "20px" }}
      >
        <Typography variant="h6" gutterBottom>
          Documents (Optional)
        </Typography>
        <Divider style={{ marginBottom: "15px" }} />

        <Box mb={2}>
          <input
            accept="*/*"
            style={{ display: "none" }}
            id="documents-upload"
            type="file"
            multiple
            onChange={handleFileUpload}
          />
          <label htmlFor="documents-upload">
            <Button
              variant="outlined"
              component="span"
              startIcon={<AttachFileIcon />}
            >
              Upload Documents
            </Button>
          </label>
          <Typography
            variant="caption"
            display="block"
            mt={1}
            color="textSecondary"
          >
            Files will be automatically converted to base64 format for XML
          </Typography>
        </Box>

        {documents.length > 0 && (
          <List>
            {documents.map((doc, index) => (
              <Paper
                key={index}
                style={{ padding: "15px", marginBottom: "10px" }}
                elevation={1}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Sequence Number"
                      value={doc.sequence_number}
                      onChange={(e) =>
                        handleDocumentFieldChange(
                          index,
                          "sequence_number",
                          e.target.value
                        )
                      }
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="File Type"
                      value={doc.file_type}
                      onChange={(e) =>
                        handleDocumentFieldChange(
                          index,
                          "file_type",
                          e.target.value
                        )
                      }
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="File Name"
                      value={doc.file_name || ""}
                      onChange={(e) =>
                        handleDocumentFieldChange(
                          index,
                          "file_name",
                          e.target.value
                        )
                      }
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="caption" color="textSecondary">
                        Base64 content size:{" "}
                        {((doc.file_content.length * 0.75) / 1024).toFixed(2)} KB
                      </Typography>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleDeleteDocument(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </List>
        )}
      </Paper>

      <Box display="flex" gap={2} justifyContent="center" mt={3}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SendIcon />}
          onClick={handleSubmit}
          disabled={!sequenceNumber || !documentType || !justification}
        >
          Trimite CC446
        </Button>
      </Box>
    </div>
  );
}
